// @flow

import React, { type Node } from 'react';
// $FlowFixMe
import Select from 'react-select';
// $FlowFixMe
import VirtualizedSelect from 'react-virtualized-select';
import { injectGlobal } from 'styled-components';
import classNames from 'classnames';

import './select.css';
// $FlowFixMe
import 'react-virtualized-select/styles.css';
import { Chip } from '../chip';
import { fixKeys, filterOptions, copyOptions } from './utils';
import { buildOptionGroupRenderer } from './option_group_menu_renderer';
import { virtualizedSelectOptionRenderer } from './virtualized_select_option_renderer';
import type { Props } from '../dropdown_select/types';
import { colors, fontWeight } from '../../constants';

const defaultProps = {
  noResultsText: 'No matches found',
  loadingPlaceholder: 'Loading...,',
  searchPromptText: 'Type to search...',
};

type LegacyDropdownProps = Props & {
  loadingPlaceholder?: Node,
  searchPromptText?: Node,
  noResultsText?: Node,
};

const LegacyDropdown = (props: LegacyDropdownProps) => {
  const onFilterChange = (options, filter, currentValues) => {
    // filterOptions will mutate our original options, so we much copy them first
    let deepCopyOptions = copyOptions(options);
    deepCopyOptions = fixKeys(deepCopyOptions);
    return filterOptions(deepCopyOptions, filter, currentValues);
  };

  const newProps = () => {
    const newPropsOptions = {
      ...props,

      filterOptions: onFilterChange,

      menuRenderer: buildOptionGroupRenderer({
        allowCategorySelection: true,
        selectedOptions: props.value,
        // remove from list if isMulti in order to make all parent's children disappear if the parent was selected
        removeSelected: props.isMulti,
        noResultsText: props.noResultsText,
      }),
      removeSelected: props.isMulti,
      clearable: props.isClearable,
      searchable: props.isSearchable,
      multi: props.isMulti,
      className: classNames(props.className, 'legacy-select'),
      disabled: props.isDisabled,
      valueComponent: props.isMulti ? renderOptionsAsChips : undefined,
    };

    return newPropsOptions;
  };

  const renderOptionsAsChips = (config) => {
    // we only care about custom rendering the "chips" or multi-select values
    // if not, use the default renderer

    // you *need* to bind them to onMouseDown
    // because when you click on an item
    // react-select opens the menu and kills the event propagation
    const onMouseDown = (evt) => {
      // prevent react-select from opening the menu on click
      evt.stopPropagation();

      if (!config.disabled) {
        if (config.onClick) {
          config.onClick(evt);
        }
        if (config.onRemove) {
          config.onRemove(config.value);
        }
      }
    };

    const label = config.value.label;

    return (
      <Chip
        key={config.key}
        text={label}
        onRemove={onMouseDown}
        status="default"
      />
    );
  };

  if (props.isVirtualized) {
    /* passed in optionRenderer must properly handle highlighting selected & focused values properly as virtualizedSelectOptionRenderer does */
    // $FlowFixMe
    const { optionRenderer, ...rest } = newProps();
    return (
      <VirtualizedSelect
        {...rest}
        optionRenderer={optionRenderer || virtualizedSelectOptionRenderer}
      />
    );
  } else {
    return <Select {...newProps()} />;
  }
};

const TieredDropdown = (props: Props) => {
  return <LegacyDropdown {...props} isTiered isVirtualized={false} />;
};

const VirtualizedDropdown = (props: Props) => {
  return <LegacyDropdown {...props} isTiered={false} isVirtualized />;
};

LegacyDropdown.defaultProps = defaultProps;

// Do not use these dropdowns directly, use Dropdown in dropdown_select with isTiered or isVirtualized
export { TieredDropdown, VirtualizedDropdown };

injectGlobal`
  .legacy-select {
    .is-open > .Select-control,
    .Select-control{
      background-color: ${colors.white};
    }

    .Select-arrow-zone{
      padding-right:16px;
    }

    .Select-clear-zone{
      padding-right:10px;

      .Select-clear{
        font-size: 20px;
        color: ${colors.mediumGrey};
        font-weight: ${fontWeight.normal};

        &:hover{
        color: ${colors.darkestGrey};
        }
      }
    }

    .Select-option-group-label.is-disabled,
    .Select-option.is-disabled {
      color: #9b9b9b;
    }

    .Select-option-group-label, .Select-option {
      color: ${colors.darkestGrey};
      cursor: pointer;

      &.is-focused {
        background: white;
      }

      &:hover {
        background: ${colors.babyBlue};
      }

      &.is-disabled:hover {
        background: ${colors.white};
        cursor: default;
      }

      &.is-selected{
        background: ${colors.lighterGrey};
      }
    }

    .Select-option-group .Select-option {
      margin-left: 15px !important;
    }

    .Select-option-group-label {
      padding-left: 15px !important;
    }

    .Select-option {
      padding-left: 15px !important;
    }

    &.has-value.Select--multi .Select-multi-value-wrapper{
      padding-left: 7px;
    }

    .Select-loading-zone{
      padding-right:7px;

      .Select-loading{
        border-color: rgba(0,178,169,0.05);
        border-left-color: ${colors.mediumGreenhouseGreen};
        animation-duration: 1.1s;
        width:18px;
        height:18px;
      }
    }

    .VirtualizedSelectSelectedOption{
      background: ${colors.lighterGrey};
      font-weight: ${fontWeight.normal};
    }
  }

`;
